import { Button, Divider, Flex, Tooltip } from 'antd';
import { FiLogOut, FiSave, FiSkipBack, FiSkipForward, FiTrash2, FiXCircle } from 'react-icons/fi';
import { LuLightbulb } from 'react-icons/lu';

import BaseText from '@components/typography/BaseText';

import AnnotationDataSwitch from './AnnotationDataSwitch';
import DrawToolPicker from './DrawToolPicker';
import { useCompactToolbar, useWideToolbar } from './useCompactToolbar';

/**
 * A toolbar button: its label beside the icon (none when `iconOnly`, or in the
 * compact toolbar), and a tooltip naming it with its shortcut. The label stays the
 * accessible name either way. Wrapped so the tooltip shows on a disabled button.
 */
const ToolButton = ({ label, title = label, shortcut, keys, compact, iconOnly, ...props }) => (
	<Tooltip title={shortcut ? `${title} (${shortcut})` : title}>
		<span>
			<Button aria-label={label} aria-keyshortcuts={keys} {...props}>
				{compact || iconOnly ? null : label}
			</Button>
		</span>
	</Tooltip>
);

/**
 * The bottom toolbar of object annotation (Collection view): Exit, the draw
 * tools, where the session is, and the tile actions. Save sends the open
 * tile to the backend (adds, corrections of model boxes); a tile with nothing
 * to send is saved as empty. Train New Object opens the training-data modal for the
 * active object (`useTrainNewObject`); its tooltip says why it is off, when it is.
 *
 * Two session shapes share it. Tile-driven (orthophoto): the user opens a tile
 * from the map; Save is the way out of a tile with changes, and `onCancel` lets
 * the open tile be left at any time, discarding what was not saved. No Continue.
 * Sequential (single image): `onPrevious` / `onNext` step through the images
 * (each disabled at its end; leaving an image with unsaved changes asks first), Save
 * saves the open image and stays on it, and `onCancel` leaves annotation mode.
 *
 * Both flows edit select-first: the `mode` picker (Draw / Select) is what they pass.
 * The `drawing` / `startDraw` / `startErase` picker is the legacy Draw / Erase pair, kept
 * for callers that still use it.
 *
 * The data switch (`AnnotationDataSwitch`), at the right end behind a divider,
 * picks which boxes the map shows: all, the annotations, or the detections.
 *
 * Fills the map toolbar's container, which stays the fixed bar at the bottom, in
 * one row: shortcuts live in the tooltips, Previous / Next / Rotate are icons, the
 * status is short (in full in its tooltip), and below the `xl` breakpoint every
 * button is an icon (`useCompactToolbar`); Delete keeps its label only from `xxl`
 * up (`useWideToolbar`).
 *
 * @param {() => void} props.onExit
 * @param {boolean} [props.drawing] - false while the eraser is active (legacy picker)
 * @param {() => void} [props.startDraw] - legacy picker
 * @param {() => void} [props.startErase] - legacy picker
 * @param {'draw'|'select'} [props.mode] - Draw / Select mode picker (replaces the legacy picker)
 * @param {(mode: 'draw'|'select') => void} [props.onModeChange]
 * @param {{ index: number|null, total: number, unit?: 'tile'|'image', saved?: number }} props.progress -
 *   zero-based open tile (null when none is open), and how many are saved
 * @param {() => void} [props.onRemove] - removes the selected box (object sessions)
 * @param {boolean} [props.canRemove] - a box is selected
 * @param {() => void} props.onSave - saves the open tile
 * @param {boolean} [props.canSave] - the open tile changed, or was never saved
 * @param {boolean} [props.savesEmpty] - saving the open tile sends no rows (an empty mark)
 * @param {string} [props.objectName] - the object being annotated, for the Save tooltip
 * @param {() => void} [props.onCancel] - leave the open tile (tile-driven), or annotation mode (sequential)
 * @param {() => void} [props.onPrevious] - sequential sessions only: back to the previous image
 * @param {() => void} [props.onNext] - sequential sessions only: on to the next image
 * @param {boolean} [props.disabled] - tools and tile actions, while something loads or no tile is open
 * @param {boolean} [props.loading] - spinner on the tile actions
 * @param {boolean} [props.keysDisabled] - a dialog is open over the page: the draw-tool
 *   keys stand down
 * @param {boolean} [props.saveEmphasis] - the open tile has unsaved boxes: Save becomes the
 *   main action. Without them Save stays outlined (it can still mark the tile checked), so it
 *   does not compete with Train New Object
 * @param {() => void} [props.onTrain] - opens Train New Object; without it the button is off
 * @param {boolean} [props.trainDisabled]
 * @param {string} [props.trainTooltip] - why Train New Object is off, or what it does
 */
const AnnotationToolbar = ({
	onExit,
	drawing,
	startDraw,
	startErase,
	mode,
	onModeChange,
	onRemove,
	canRemove = false,
	progress,
	onSave,
	canSave = false,
	savesEmpty = false,
	objectName = 'this object',
	onCancel,
	onPrevious,
	onNext,
	disabled = false,
	loading = false,
	keysDisabled = false,
	saveEmphasis = false,
	onTrain,
	trainDisabled = false,
	trainTooltip,
}) => {
	const { index, total, unit = 'tile', saved } = progress;
	const isOpen = index !== null && index !== undefined;
	const isLast = isOpen && index >= total - 1;
	const unitLabel = unit === 'image' ? 'Image' : 'Tile';
	const unitPlural = `${unit}s`;

	const compact = useCompactToolbar();
	const wide = useWideToolbar();

	// Short in the bar, in full on hover.
	const where = isOpen ? `${unitLabel} ${index + 1}/${total}` : `${saved ?? 0}/${total} saved`;
	const whereInFull = isOpen
		? `${unitLabel} ${index + 1} of ${total}`
		: `${saved ?? 0} of ${total} ${unitPlural} saved · pick a ${unit} on the map`;

	const saveTitle = canSave
		? savesEmpty
			? `Save this ${unit} as empty`
			: `Save the ${objectName} boxes in this ${unit}`
		: isOpen
			? `Nothing new to save in this ${unit}`
			: `Open a ${unit} first`;

	return (
		<Flex
			justify="flex-end"
			align="center"
			gap="middle"
			wrap={false}
			role="toolbar"
			aria-label="Annotation tools"
			style={{ width: '100%' }}>
			<ToolButton
				label="Exit"
				shortcut="Esc"
				keys="Escape"
				icon={<FiLogOut />}
				onClick={onExit}
				compact={compact}
			/>

			<DrawToolPicker
				drawing={drawing}
				startDraw={startDraw}
				startErase={startErase}
				showErase={Boolean(startErase)}
				mode={mode}
				onModeChange={onModeChange}
				disabled={disabled}
				compact={compact}
				keysDisabled={keysDisabled}
			/>

			{onRemove && (
				<ToolButton
					label="Delete"
					title={canRemove ? 'Remove the selected box' : 'Select a box to remove it'}
					shortcut="Del"
					keys="Delete Backspace"
					icon={<FiTrash2 />}
					onClick={onRemove}
					disabled={disabled || !canRemove}
					compact={compact}
					iconOnly={!wide}
				/>
			)}

			<Flex align="center" gap="middle" wrap={false} style={{ minWidth: 0 }}>
				<Tooltip title={whereInFull}>
					{/* Shortens with "…" rather than breaking the row on a narrow screen. */}
					<BaseText type="secondary" ellipsis style={{ minWidth: 0 }}>
						{where}
					</BaseText>
				</Tooltip>

				{onCancel && isOpen && (
					<ToolButton
						label="Cancel"
						icon={<FiXCircle />}
						onClick={onCancel}
						disabled={loading}
						compact={compact}
					/>
				)}

				{onPrevious && total > 1 && (
					<ToolButton
						label="Previous"
						title={`Previous ${unit}`}
						shortcut="Space"
						keys="Space"
						icon={<FiSkipBack />}
						onClick={onPrevious}
						disabled={disabled || !isOpen || index === 0}
						iconOnly
					/>
				)}

				{onNext && total > 1 && (
					<ToolButton
						label="Next"
						title={`Next ${unit}`}
						shortcut="Enter"
						keys="Enter"
						icon={<FiSkipForward />}
						onClick={onNext}
						disabled={disabled || !isOpen || isLast}
						iconOnly
					/>
				)}

				<ToolButton
					label={`Save ${unit}`}
					title={saveTitle}
					type={saveEmphasis ? 'primary' : 'default'}
					icon={<FiSave />}
					onClick={onSave}
					disabled={disabled || !canSave}
					loading={loading}
					compact={compact}
				/>
				<ToolButton
					label="Train New Object"
					title={trainTooltip}
					type="primary"
					icon={<LuLightbulb />}
					onClick={onTrain}
					disabled={!onTrain || trainDisabled || loading}
					compact={compact}
				/>
			</Flex>

			<Divider orientation="vertical" style={{ height: 'auto', alignSelf: 'stretch' }} />

			<AnnotationDataSwitch />
		</Flex>
	);
};

export default AnnotationToolbar;
